import * as React from 'react'
import produce from 'immer'
import cx from 'clsx'
import { Form, Input, Button, Spin, Tabs, Dropdown, Menu, Tooltip, message } from 'antd'
import { isNil, set, cloneDeep, isEqual, flow } from 'lodash'
import { withRouter } from 'react-router-dom'
import { showError, ValidationError } from 'helpers/errors'
import { getUserPermission, allowPricing } from 'helpers/auth'
import { createLabelFactory, createFieldFactory, createHandleSubmit, isReadOnly } from 'helpers/formViews'
import linkTargets from 'options/linkTargets'
import { t } from 'helpers/i18n'
import { Emitter, stopEvent } from 'helpers/events'
import { FORM_CHANGE_EVENT, FORM_SUBMIT_EVENT } from 'options/events'
import { PRIVATE_DATABASE } from 'options/products'
import Modal from 'elements/Modal'
import Icon from 'elements/Icon'
import InputNumber from 'elements/InputNumber'
import DatePicker from 'elements/DatePicker'
import SelectProducts from 'containers/Products/Select'
import Drawer from 'elements/Drawer'
import FormButtons from 'elements/FormButtons'
import InputImage from 'elements/InputImage'
import TransferOverstockInventory from 'containers/OverstockInventory/Transfer'
import { Row, Col } from 'elements/Grid'

const ProductsFormView = linkTargets.productRecord.formComponent

class FormView extends React.Component {
  state = {}

  constructor(props) {
    super(props)

    this.handleSubmitDefault = createHandleSubmit(this)
  }

  componentDidMount() {
    const { selectedProduct } = this.props

    if (selectedProduct) {
      this.setState({ selectedProduct }, this.handleSelectProductOk)
    } else {
      this.fetchItem()
    }

    Emitter.on(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  componentWillUnmount() {
    Emitter.off(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  handleSubmit = (e, options) => {
    stopEvent(e)

    if (
      this.state.item.id &&
      this.props.form.getFieldValue('onHand') === 0 &&
      this.props.form.getFieldValue('onHand2') === 0
    ) {
      Modal.confirm({
        autoFocusButton: 'ok',
        title: t('confirmDeleteForZeroOnHand'),
        okText: t('continue'),
        okType: 'primary',
        cancelText: t('cancel'),
        onOk: async () => {
          try {
            await this.handleSubmitDefault(e, options)
            this.props.onCancel()
          } catch (error) {
            console.error(error)
          }
        },
      })
    } else {
      this.handleSubmitDefault(e, options)
    }
  }

  promiseState = (state = {}) => new Promise((resolve) => this.setState(state, resolve))

  handleFormChange = () => this.setState({ formHasChanged: true })

  handleSelectProductOk = async () => {
    const { selectedProduct, selectedProductFilterDto } = this.state

    if (selectedProduct) {
      const {
        barcode,
        itemNumber,
        manufacturer,
        description: itemDescription,
        price,
        packageSizeUom,
        preferredSupplierName: itemSupplierName,
        reworkTypeDescription,
      } = selectedProduct

      const catalogTableName = selectedProductFilterDto?.catalogTableName ?? PRIVATE_DATABASE

      this.setState({ selectProductVisible: false })

      try {
        if (catalogTableName !== PRIVATE_DATABASE && selectedProduct) {
          await this.props
            .copyToPrivate({
              catalogProductIds: [selectedProduct.id],
              catalogTableName,
            })
            .catch(() => {})
        }

        const locationGroups = await this.props.getLocationGroups().then((r) => r.value.data.items)
        const { overstockLocationId, overstockLocationName } = locationGroups.find(
          (one) => one.id === this.props.filterDto?.locationGroupId
        )

        const responses = await Promise.all([
          this.props.newItem({
            barcode,
            itemNumber,
            manufacturer,
            itemDescription,
            price,
            packageSizeUom,
            itemSupplierName,
            reworkTypeDescription,
            onHand: 1,
            onHand2: 0,
            overstockLocationId,
            overstockLocationName,
          }),
          this.props.getSettings({ type: this.props.settingsType }),
          this.props.getLocationGroups(),
        ])

        this.setState({
          item: cloneDeep(responses[0].value.data),
          itemOriginal: cloneDeep(responses[0].value.data),
          fieldSettings: responses[1].value.data.fieldSettings,
          formHasChanged: false,
        })
      } catch (error) {
        showError({ error })
      }
    }
  }

  getItemId = () => [this.state.item?.id, this.props.linkTargetRecord?.overstockInventoryId].find(Boolean)

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    if (isNil(itemId) && !this.props.linkTargetRecord) {
      this.setState({ selectProductVisible: true })
    } else {
      try {
        const responses = await Promise.all([
          this.props.getItem(itemId).catch((error) => {
            this.props.onCancel?.()
            throw error
          }),
          this.props.getSettings({ type: this.props.settingsType }),
        ])

        const item = cloneDeep(responses[0].value.data)

        const image =
          item.id && item.productId
            ? await this.props
                .getDocumentContents({
                  request: {
                    domainObjectId: item.productId,
                    domainObjectType: 'Product',
                    documentType: 'ObjectImage',
                    documentName: item.barcode,
                  },
                })
                .catch(() => {})
            : null

        this.setState({
          item,
          itemOriginal: cloneDeep(item),
          fieldSettings: responses[1].value.data.fieldSettings,
          image: cloneDeep(image?.value?.data),
          imageOriginal: cloneDeep(image?.value?.data),
          formHasChanged: false,
        })
      } catch (error) {
        showError({ error })
      }
    }
  }

  handleActionsMenuClick = ({ key }) => {
    switch (key) {
      case 'transferToInventory':
        if (this.hasUnsavedChanges()) {
          message.error(t('saveChangesFirst'))
        } else {
          this.setState({ transferOverstockInventoryVisible: true })
        }
        break

      default:
        message.warn(t('underDevelopment'))
        break
    }
  }

  setItemValue = (name, value) => {
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        this.props.form.setFieldsValue({ [name]: value })
      })
    )
  }

  hasUnsavedChanges = () =>
    this.state.formHasChanged || !this.state.item?.id || !isEqual(this.state.item, this.state.itemOriginal)

  render() {
    const { onCancel, tenantTypeIsTrms } = this.props
    const { item, selectedProduct, fieldSettings } = this.state

    if (!item || !fieldSettings) {
      return (
        <>
          <Spin />
          <Modal
            title={t('selectProduct')}
            visible={this.state.selectProductVisible}
            okText={t('select')}
            onOk={this.handleSelectProductOk}
            okButtonProps={{ disabled: !selectedProduct }}
            onCancel={onCancel}
            width={992}
          >
            <SelectProducts
              onSelect={(values) => this.setState({ selectedProduct: values[0] })}
              onFilter={(filterDto) => this.setState({ selectedProductFilterDto: filterDto })}
            />
          </Modal>
        </>
      )
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)
    const readOnly = isReadOnly(this)

    return (
      <>
        <Form layout="vertical" onSubmit={readOnly ? stopEvent : this.handleSubmit} colon={false}>
          {item.id ? (
            <Dropdown
              overlay={
                <Menu onClick={this.handleActionsMenuClick}>
                  <Menu.Item key="transferToInventory">{t('transferToInventory')}</Menu.Item>
                </Menu>
              }
              trigger={['click']}
            >
              <Button className="form-items-actions">
                {t('actions')}
                <Icon type="KeyboardArrowDown" />
              </Button>
            </Dropdown>
          ) : null}
          <div className="form-items-container">
            <ValidationError errors={this.state.validationErrors} />
            <Row>
              <Col md={8} xs={24}>
                <Form.Item label={t('barcode')}>
                  {['View', 'Edit'].includes(getUserPermission('Products')) && (
                    <div className="tofino-label-buttons">
                      <Tooltip
                        title={t(getUserPermission('Products') === 'Edit' ? 'editProduct' : 'viewProduct')}
                      >
                        <Button
                          size="small"
                          onClick={() => {
                            if (this.hasUnsavedChanges()) {
                              message.error(t('saveChangesFirst'))
                            } else {
                              this.setState({
                                productDrawerVisible: true,
                                productDrawerSaving: false,
                              })
                            }
                          }}
                        >
                          <Icon type="Edit" />
                        </Button>
                      </Tooltip>
                    </div>
                  )}
                  <Input value={item.barcode} disabled />
                </Form.Item>
              </Col>
              <Col md={16} xs={24}>
                <Form.Item label={createLabel('itemDescription')}>
                  <Input value={item.itemDescription} disabled />
                </Form.Item>
              </Col>
            </Row>
            <Tabs
              activeKey={this.state.tabsActiveKey || 'overstock'}
              onChange={(tabsActiveKey) => this.setState({ tabsActiveKey })}
            >
              <Tabs.TabPane key="overstock" tab={t('overstock')} forceRender>
                <Row>
                  <Col md={8} xs={24}>
                    <Form.Item label={createLabel('overstockLocationName')}>
                      <Input value={item.overstockLocationName} placeholder={t('none')} disabled />
                    </Form.Item>
                    <Form.Item label={createLabel('binLocation')}>
                      {createFieldDecorator('binLocation')(<Input readOnly={readOnly} />)}
                    </Form.Item>
                  </Col>
                  <Col md={8} xs={24}>
                    <Form.Item label={createLabel('onHand')} required={!item.id}>
                      {createFieldDecorator('onHand')(
                        <InputNumber readOnly={readOnly} min={item.id ? 0 : 1} />
                      )}
                    </Form.Item>
                    <Form.Item label={createLabel('onHand2')} className={cx({ hidden: item.onHand2 === 0 })}>
                      {createFieldDecorator('onHand2')(<InputNumber readOnly={readOnly} min={0} />)}
                    </Form.Item>
                  </Col>
                  <Col md={8} xs={24}>
                    <Form.Item label={createLabel('lastCountDate')}>
                      <DatePicker
                        value={item.lastCountDate}
                        placeholder={t('none')}
                        readOnly={readOnly}
                        disabled
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Tabs.TabPane>
              <Tabs.TabPane key="item" tab={t('item')} forceRender>
                <Row>
                  <Col md={16} xs={24}>
                    <Row>
                      <Col md={12} xs={24}>
                        <Form.Item label={createLabel('barcode')}>
                          <Input value={item.barcode} disabled />
                        </Form.Item>
                        <Form.Item label={createLabel('itemNumber')}>
                          <Input value={item.itemNumber} disabled />
                        </Form.Item>
                        <Form.Item label={createLabel('packageSizeUom')}>
                          <Input value={item.packageSizeUom} disabled />
                        </Form.Item>
                      </Col>
                      <Col md={12} xs={24}>
                        {tenantTypeIsTrms && (
                          <Form.Item label={createLabel('itemSupplierName')}>
                            <Input value={item.itemSupplierName} disabled />
                          </Form.Item>
                        )}
                        <Form.Item label={createLabel('manufacturer')}>
                          <Input value={item.manufacturer} disabled />
                        </Form.Item>
                        {allowPricing() && (
                          <Form.Item label={createLabel('price')}>
                            <InputNumber value={item.price} currency disabled />
                          </Form.Item>
                        )}
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <Form.Item label={createLabel('itemDescription')}>
                          <Input.TextArea value={item.itemDescription} rows={4} disabled />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                  <Col md={8} xs={24}>
                    {tenantTypeIsTrms && (
                      <Form.Item label={createLabel('reworkTypeDescription')}>
                        <Input value={item.reworkTypeDescription} disabled />
                      </Form.Item>
                    )}
                    <InputImage
                      label={t('productImage')}
                      value={this.state.image}
                      height={tenantTypeIsTrms ? '281px' : '373px'}
                      showButtons={false}
                    />
                  </Col>
                </Row>
              </Tabs.TabPane>
            </Tabs>
          </div>
          <div className="form-buttons-container">
            <FormButtons
              readOnly={readOnly}
              onCancel={this.props.onCancel}
              onSubmit={this.handleSubmit}
              saveButtonLoading={this.state.saveButtonLoading}
              saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
            />
          </div>
        </Form>
        <Modal
          title={`${t('transferToInventory')} - ${this.state.item.barcode}`}
          visible={this.state.transferOverstockInventoryVisible}
          onCancel={() => this.setState({ transferOverstockInventoryVisible: false })}
          okText={t('transfer')}
          onOk={() => Emitter.emit(FORM_SUBMIT_EVENT)}
          okButtonProps={{ loading: this.state.transferOverstockInventoryLoading }}
          width={750}
        >
          <TransferOverstockInventory
            parentRecord={{ ...item, overstockInventoryId: item.id }}
            onSave={(pending) =>
              this.promiseState({ transferOverstockInventoryLoading: pending }).then(() => {
                this.props.onSave?.(pending)
              })
            }
            onSaveAndClose={() => this.setState({ transferOverstockInventoryVisible: false }, this.fetchItem)}
            onCancel={() => this.setState({ transferOverstockInventoryVisible: false })}
          />
        </Modal>
        <Drawer
          title={`${t('product')} - ${item.barcode}`}
          size={linkTargets.productRecord.formSize}
          visible={this.state.productDrawerVisible}
          onClose={() => this.setState({ productDrawerVisible: false })}
          saving={this.state.productDrawerSaving}
        >
          <ProductsFormView
            linkTargetRecord={item ? { productId: item.productId } : undefined}
            onSave={(pending) =>
              this.promiseState({ productDrawerSaving: pending }).then(async () => {
                await this.props.onSave?.(pending)

                if (!pending) {
                  await this.fetchItem()
                }
              })
            }
            onSaveAndClose={() => this.setState({ productDrawerVisible: false })}
            onCancel={() => this.setState({ productDrawerVisible: false })}
            readOnly={getUserPermission('Products') !== 'Edit'}
          />
        </Drawer>
      </>
    )
  }
}

export default flow(
  withRouter,
  Form.create({
    onValuesChange: (...params) => Emitter.emit(FORM_CHANGE_EVENT, ...params),
  })
)(FormView)
